/* =====================================================================
   VEREDA · script global (sin librerías)
   Reveal al hacer scroll · cabecera · barra de avisos · menú móvil · ventanas · parallax · popup · contador del carrito
   Cada módulo comprueba que su elemento existe, así el mismo archivo sirve en todas las páginas.
   ===================================================================== */
(() => {
  'use strict';

  const html = document.documentElement;
  // 26/10 (research/19 A4): dentro de las apps de Facebook e Instagram el pago rápido (PayPal) falla. La marca se pone aquí, al
  // cargar cada página, porque el cajón se repinta por dentro al añadir y el script que llevaba no llega a ejecutarse.
  if (/FBAN|FBAV|FB_IAB|FBIOS|Instagram/.test(navigator.userAgent)) html.classList.add('mt-app-social');
  const reducido = window.matchMedia('(prefers-reduced-motion: reduce)');
  const $ = (sel, raiz = document) => raiz.querySelector(sel);
  const $$ = (sel, raiz = document) => Array.from(raiz.querySelectorAll(sel));

  /* ---------- utilidades compartidas ---------- */
  const tienda = window.mtTienda || {};
  const formato = new Intl.NumberFormat('es-ES', { style: 'currency', currency: tienda.moneda || 'EUR' });
  window.mtDinero = (centimos) => formato.format((Number(centimos) || 0) / 100);

  // Dos formas de escalera (ver snippets/mt-pack-calculo.liquid):
  // "2+1,3+2,4+3" → reglas «compra X, llévate Y gratis» · "1:0,3:20,6:33" → porcentaje por tramo
  const tramosEscalera = () => String(tienda.escalera || '1:0').split(',').map((t) => t.trim()).filter(Boolean);
  window.mtReglasGratis = () =>
    tramosEscalera()
      .filter((t) => t.includes('+'))
      .map((t) => {
        const [compra, regalo] = t.split('+').map((v) => parseInt(v, 10) || 0);
        return { compra, regalo };
      })
      .filter((r) => r.compra > 0 && r.regalo > 0);

  // "1:0,3:20,6:33" → [{ n: 1, pct: 0 }, …] ordenado por pares
  window.mtEscalera = () =>
    tramosEscalera()
      .filter((t) => !t.includes('+'))
      .map((tramo) => {
        const [n, pct] = tramo.split(':').map((v) => parseFloat(v));
        return { n: n || 1, pct: pct || 0 };
      })
      .sort((a, b) => a.n - b.n);

  // Pares gratis que da Shopify por N pares: cada regla se repite en el pedido y se aplica la que más regala.
  window.mtGratis = (pares) =>
    window.mtReglasGratis().reduce((max, r) => Math.max(max, Math.floor(pares / (r.compra + r.regalo)) * r.regalo), 0);

  // Descuento (%) que corresponde a un número de pares (solo en la escalera de porcentajes)
  window.mtDescuento = (pares) => {
    if (window.mtReglasGratis().length) return 0;
    let pct = 0;
    window.mtEscalera().forEach((t) => {
      if (pares >= t.n) pct = t.pct;
    });
    return pct;
  };

  const bloquearScroll = (bloquear) => html.classList.toggle('mt-bloqueo', bloquear);

  function atraparFoco(contenedor, evento) {
    if (evento.key !== 'Tab') return;
    const focos = $$('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', contenedor).filter((el) => el.offsetParent !== null);
    if (!focos.length) return;
    const primero = focos[0];
    const ultimo = focos[focos.length - 1];
    if (evento.shiftKey && document.activeElement === primero) {
      evento.preventDefault();
      ultimo.focus();
    } else if (!evento.shiftKey && document.activeElement === ultimo) {
      evento.preventDefault();
      primero.focus();
    }
  }

  /* ---------- 1 · reveal al hacer scroll ---------- */
  let observador;
  function iniciarReveal(raiz = document) {
    const elementos = $$('.mt-rev:not(.es-visible)', raiz);
    if (!elementos.length) return;
    $$('[data-mt-escalonado]', raiz).forEach((grupo) => {
      $$(':scope > .mt-rev, :scope > * > .mt-rev', grupo).forEach((el, i) => el.style.setProperty('--mt-i', Math.min(i, 6)));
    });
    if (reducido.matches || !('IntersectionObserver' in window)) {
      elementos.forEach((el) => el.classList.add('es-visible'));
      return;
    }
    if (!observador) {
      observador = new IntersectionObserver(
        (entradas) => {
          entradas.forEach((entrada) => {
            if (!entrada.isIntersecting) return;
            entrada.target.classList.add('es-visible');
            observador.unobserve(entrada.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }
    elementos.forEach((el) => observador.observe(el));
  }

  /* ---------- 2 · cabecera: se queda pegada, se esconde al bajar y vuelve al subir ---------- */
  function iniciarCabecera() {
    // El grupo se busca cada vez: el editor de temas sustituye el elemento al volver a pintar la sección.
    const grupoActual = () => $('.mt-cabecera-grupo');
    if (!grupoActual()) return;
    let ultimo = window.scrollY;
    let pendiente = false;
    // Altura de lo que hay encima de la cabecera (la barra de avisos). Se mide aparte porque el
    // offsetTop de un elemento pegado cambia con el scroll.
    let tope = 0;
    const medir = () => {
      tope = 0;
      const grupo = grupoActual();
      let previo = grupo && grupo.previousElementSibling;
      while (previo) {
        if (previo.classList.contains('shopify-section')) tope += previo.offsetHeight;
        previo = previo.previousElementSibling;
      }
    };
    medir();
    window.addEventListener('resize', medir, { passive: true });
    document.addEventListener('shopify:section:load', medir);
    const actualizar = () => {
      pendiente = false;
      const grupo = grupoActual();
      if (!grupo) return;
      const y = window.scrollY;
      grupo.classList.toggle('es-pegada', y > tope + 4);
      // Con el menú, el popup u otra ventana abiertos la cabecera no se mueve.
      const ventanaAbierta = html.classList.contains('mt-bloqueo') || html.classList.contains('mt-menu-abierto');
      if (!ventanaAbierta && y > tope + 260 && y > ultimo + 6) grupo.classList.add('es-oculta');
      else if (y < ultimo - 6 || y <= tope + 260) grupo.classList.remove('es-oculta');
      ultimo = y;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (pendiente) return;
        pendiente = true;
        requestAnimationFrame(actualizar);
      },
      { passive: true }
    );
    // Quien navega con el teclado no debe quedarse con el foco en una cabecera escondida.
    document.addEventListener('focusin', (e) => {
      const grupo = e.target.closest ? e.target.closest('.mt-cabecera-grupo') : null;
      if (grupo) grupo.classList.remove('es-oculta');
    });
    actualizar();
  }

  /* ---------- 3 · barra de avisos ---------- */
  // Un mensaje a la vez, en todos los anchos. Rota sola cada «pausa» segundos y se detiene con el ratón encima,
  // con el foco dentro, al tocarla, cuando no está a la vista y con la pestaña oculta. Con un solo mensaje no hace nada.
  // El movimiento es de CSS (solo transform/opacity); con «reducir movimiento» el cambio es instantáneo.
  function iniciarAnuncio() {
    const enEditor = !!(window.Shopify && window.Shopify.designMode);

    function montar(barra) {
      if (barra.mtAnuncio) return barra.mtAnuncio;
      const lista = $('.mt-anuncio__lista', barra);
      const items = $$('[data-mt-anuncio-item]', barra);
      const api = { ir() {}, pausar() {}, reanudar() {} };
      barra.mtAnuncio = api;
      if (!lista || items.length < 2) return api;

      const pausa = Math.min(10, Math.max(3, parseFloat(barra.dataset.pausa) || 4)) * 1000;
      const motivos = new Set(); // por qué está parada: raton · foco · toque · fuera · editor
      let indice = Math.max(0, items.findIndex((el) => el.classList.contains('es-activo')));
      let reloj = 0;
      let relojToque = 0;

      const mostrar = (destino, sentido) => {
        const nuevo = (destino + items.length) % items.length;
        if (nuevo === indice) return;
        items.forEach((el) => el.classList.remove('es-sale'));
        barra.style.setProperty('--mt-anuncio-d', sentido < 0 ? '-10px' : '10px');
        void lista.offsetWidth; // los mensajes ocultos se colocan en su punto de partida antes de animar
        items[indice].classList.remove('es-activo');
        items[indice].classList.add('es-sale');
        items[nuevo].classList.add('es-activo');
        indice = nuevo;
      };
      const programar = () => {
        clearTimeout(reloj);
        if (motivos.size || document.hidden || !barra.isConnected) return;
        reloj = setTimeout(() => {
          mostrar(indice + 1, 1);
          programar();
        }, pausa);
      };
      // Mientras alguien la maneja con el teclado (o en el editor), los cambios se anuncian; cuando rota sola, no.
      const anunciar = () => lista.setAttribute('aria-live', motivos.has('foco') || motivos.has('editor') ? 'polite' : 'off');

      api.pausar = (motivo) => {
        motivos.add(motivo);
        clearTimeout(reloj);
        anunciar();
      };
      api.reanudar = (motivo) => {
        motivos.delete(motivo);
        anunciar();
        programar();
      };
      api.ir = (i) => {
        mostrar(i, i < indice ? -1 : 1);
        programar();
      };

      barra.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') api.pausar('raton');
      });
      barra.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse') api.reanudar('raton');
      });
      barra.addEventListener(
        'pointerdown',
        (e) => {
          if (e.pointerType === 'mouse') return;
          api.pausar('toque');
          clearTimeout(relojToque);
          relojToque = setTimeout(() => api.reanudar('toque'), Math.max(pausa, 6000));
        },
        { passive: true }
      );
      barra.addEventListener('focusin', () => api.pausar('foco'));
      barra.addEventListener('focusout', (e) => {
        if (!barra.contains(e.relatedTarget)) api.reanudar('foco');
      });

      $$('[data-mt-anuncio-ir]', barra).forEach((boton) => {
        boton.hidden = false;
        boton.addEventListener('click', () => {
          const paso = parseInt(boton.dataset.mtAnuncioIr, 10) < 0 ? -1 : 1;
          mostrar(indice + paso, paso);
          programar();
        });
      });

      const alCambiarPestana = () => {
        if (barra.isConnected) programar();
        else document.removeEventListener('visibilitychange', alCambiarPestana);
      };
      document.addEventListener('visibilitychange', alCambiarPestana);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entradas) => {
          if (entradas[entradas.length - 1].isIntersecting) api.reanudar('fuera');
          else api.pausar('fuera');
        }).observe(barra);
      }
      anunciar();
      programar();
      return api;
    }

    $$('[data-mt-anuncio]').forEach(montar);
    if (!enEditor) return;

    // Editor de temas: la barra se vuelve a montar al repintarse y, al elegir un bloque, enseña ese mensaje y se queda en él.
    document.addEventListener('shopify:section:load', (e) => $$('[data-mt-anuncio]', e.target).forEach(montar));
    document.addEventListener('shopify:block:select', (e) => {
      const item = e.target.closest('[data-mt-anuncio-item]');
      const barra = item && item.closest('[data-mt-anuncio]');
      if (!barra) return;
      const api = montar(barra);
      api.pausar('editor');
      api.ir($$('[data-mt-anuncio-item]', barra).indexOf(item));
    });
    document.addEventListener('shopify:block:deselect', (e) => {
      const barra = e.target.closest('[data-mt-anuncio]');
      if (barra && barra.mtAnuncio) barra.mtAnuncio.reanudar('editor');
    });
  }

  /* ---------- 4 · menú (cajón) ---------- */
  // Todo va por delegación: sigue funcionando cuando el editor de temas vuelve a pintar la cabecera.
  // Abierto por una persona: foco atrapado, Escape, scroll del fondo bloqueado y foco devuelto al cerrar.
  // Abierto por el editor (al elegir un bloque del menú): solo se enseña, sin atrapar nada.
  function iniciarMenu() {
    const enEditor = !!(window.Shopify && window.Shopify.designMode);
    const partes = () => {
      const menu = $('[data-mt-menu]');
      return menu ? { menu, panel: $('.mt-menu__panel', menu), boton: $('[data-mt-menu-abrir]') } : null;
    };
    let conFoco = false; // lo abrió una persona (no el editor)
    let devolver = null;

    const abrir = ({ editor = false, sinTransicion = false } = {}) => {
      const p = partes();
      if (!p || p.menu.classList.contains('es-abierto')) return;
      p.menu.classList.toggle('es-sin-transicion', sinTransicion);
      p.menu.classList.add('es-abierto');
      html.classList.add('mt-menu-abierto');
      if (p.boton) p.boton.setAttribute('aria-expanded', 'true');
      if (editor) return;
      conFoco = true;
      devolver = document.activeElement;
      { const hueco = window.innerWidth - html.clientWidth; html.style.setProperty('--mt-barra', `${hueco > 0 && hueco <= 20 ? hueco : 0}px`); }
      bloquearScroll(true);
      const primero = $('.mt-menu__panel [data-mt-menu-cerrar]', p.menu) || p.panel;
      if (primero) primero.focus({ preventScroll: true });
    };
    const cerrar = () => {
      const p = partes();
      html.classList.remove('mt-menu-abierto');
      if (!p || !p.menu.classList.contains('es-abierto')) return;
      p.menu.classList.remove('es-abierto', 'es-sin-transicion');
      if (p.boton) p.boton.setAttribute('aria-expanded', 'false');
      if (!conFoco) return;
      conFoco = false;
      bloquearScroll(false);
      const destino = devolver && devolver.isConnected && devolver !== document.body ? devolver : p.boton;
      devolver = null;
      if (destino) destino.focus({ preventScroll: true });
    };

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-mt-menu-abrir]')) {
        abrir();
        return;
      }
      const p = partes();
      if (!p || !p.menu.classList.contains('es-abierto')) return;
      // Cierra el botón, el fondo y cualquier enlace del cajón (también los que solo bajan a un ancla de la misma página).
      if (e.target.closest('[data-mt-menu-cerrar]') || e.target.closest('.mt-menu__panel a[href]')) cerrar();
    });
    document.addEventListener('keydown', (e) => {
      if (!conFoco) return;
      const p = partes();
      if (!p || !p.menu.classList.contains('es-abierto')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        cerrar();
      } else if (e.key === 'Tab' && !p.panel.contains(document.activeElement)) {
        e.preventDefault(); // el foco se había escapado: vuelve al cajón
        const primero = $('.mt-menu__panel [data-mt-menu-cerrar]', p.menu);
        if (primero) primero.focus();
      } else {
        atraparFoco(p.panel, e);
      }
    });

    if (!enEditor) {
      // Si la pantalla crece hasta el ancho con navegación en línea, el cajón sobra.
      const ancho = window.matchMedia('(min-width: 1100px)');
      const alCambiar = (e) => {
        if (e.matches) cerrar();
      };
      if (ancho.addEventListener) ancho.addEventListener('change', alCambiar);
      else if (ancho.addListener) ancho.addListener(alCambiar);
      return;
    }

    // Editor de temas: los bloques de la cabecera se señalan dentro del cajón, así que se abre al elegir uno.
    let cierre = 0;
    document.addEventListener('shopify:block:select', (e) => {
      if (!e.target.closest('.mt-menu__panel')) return;
      clearTimeout(cierre);
      abrir({ editor: true, sinTransicion: !!(e.detail && e.detail.load) });
    });
    document.addEventListener('shopify:block:deselect', (e) => {
      if (!e.target.closest('.mt-menu__panel')) return;
      clearTimeout(cierre);
      cierre = setTimeout(() => {
        if (!conFoco) cerrar();
      }, 80);
    });
    document.addEventListener('shopify:section:load', (e) => {
      if (!$('[data-mt-menu]', e.target)) return;
      // La cabecera se ha vuelto a pintar: el cajón nuevo nace cerrado.
      html.classList.remove('mt-menu-abierto');
      if (conFoco) {
        conFoco = false;
        bloquearScroll(false);
      }
    });
  }

  /* ---------- 5 · ventanas (modales) ---------- */
  let ultimoFoco = null;
  function abrirModal(modal) {
    if (!modal) return;
    ultimoFoco = document.activeElement;
    modal.hidden = false;
    bloquearScroll(true);
    const foco = $('[data-mt-foco]', modal) || $('.mt-modal__x', modal);
    if (foco) foco.focus({ preventScroll: true });
  }
  function cerrarModal(modal) {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    bloquearScroll(false);
    if (ultimoFoco) ultimoFoco.focus({ preventScroll: true });
  }
  window.mtModal = { abrir: abrirModal, cerrar: cerrarModal };
  function iniciarModales() {
    document.addEventListener('click', (e) => {
      const abre = e.target.closest('[data-mt-abrir]');
      if (abre) {
        e.preventDefault();
        abrirModal($(abre.dataset.mtAbrir));
        return;
      }
      const cierra = e.target.closest('[data-mt-cerrar]');
      if (cierra) cerrarModal(cierra.closest('.mt-modal'));
    });
    document.addEventListener('keydown', (e) => {
      const abierta = $('.mt-modal:not([hidden])');
      if (!abierta) return;
      if (e.key === 'Escape') cerrarModal(abierta);
      atraparFoco($('.mt-modal__caja', abierta), e);
    });
  }

  /* ---------- 6 · acordeones: al abrir uno se cierran los hermanos ---------- */
  function iniciarAcordeones() {
    document.addEventListener(
      'toggle',
      (e) => {
        const item = e.target;
        if (!item.matches || !item.matches('.mt-acordeon[data-mt-uno] > .mt-acordeon__item') || !item.open) return;
        $$(':scope > .mt-acordeon__item[open]', item.parentElement).forEach((otro) => {
          if (otro !== item) otro.open = false;
        });
      },
      true
    );
  }

  /* ---------- 7 · parallax suave (solo escritorio, solo transform) ---------- */
  function iniciarParallax() {
    const capas = $$('[data-mt-parallax]');
    if (!capas.length || reducido.matches || window.innerWidth < 990) return;
    let pendiente = false;
    const mover = () => {
      pendiente = false;
      const alto = window.innerHeight;
      capas.forEach((capa) => {
        const caja = capa.parentElement.getBoundingClientRect();
        if (caja.bottom < -100 || caja.top > alto + 100) return;
        const avance = (caja.top + caja.height / 2 - alto / 2) / alto; // −1 … 1
        const fuerza = parseFloat(capa.dataset.mtParallax) || 6;
        capa.style.transform = `translate3d(0, ${(-avance * fuerza).toFixed(2)}%, 0) scale(${1 + fuerza / 50})`;
      });
    };
    window.addEventListener(
      'scroll',
      () => {
        if (pendiente) return;
        pendiente = true;
        requestAnimationFrame(mover);
      },
      { passive: true }
    );
    mover();
  }

  /* ---------- 8 · popup de correo ---------- */
  // Sale cuando han pasado «espera» segundos Y la página se ha bajado «avance» %, nunca encima del menú, del carrito
  // o de otra ventana (espera a que se cierren). Tras verlo no vuelve en «dias» días; tras apuntarse, en un año.
  // Shopify recarga la página al enviar el formulario: al volver se abre directamente en la confirmación.
  // Pruebas: ?mt-popup=1 lo abre al momento · ?mt-popup=ok abre la confirmación · ?mt-popup=reset olvida que ya se vio.
  function iniciarPopup() {
    const enEditor = !!(window.Shopify && window.Shopify.designMode);
    const CLAVE = 'mt-popup-hasta'; // localStorage: fecha (ms) hasta la que no debe volver a salir
    const ENVIADO = 'mt-popup-enviado'; // sessionStorage: el formulario del popup se acaba de enviar
    const DIA = 864e5;
    const actual = () => $('[data-mt-popup]');
    const leer = (almacen, clave) => {
      try {
        return window[almacen].getItem(clave);
      } catch (e) {
        return null;
      }
    };
    const guardar = (almacen, clave, valor) => {
      try {
        if (valor === null) window[almacen].removeItem(clave);
        else window[almacen].setItem(clave, valor);
      } catch (e) {}
    };
    const enPlazo = () => Date.now() < (parseInt(leer('localStorage', CLAVE), 10) || 0);
    // Aplaza la próxima aparición; nunca acorta un plazo que ya fuera más largo.
    const aplazar = (dias) => {
      const hasta = Date.now() + dias * DIA;
      if (hasta > (parseInt(leer('localStorage', CLAVE), 10) || 0)) guardar('localStorage', CLAVE, String(hasta));
    };

    let abierto = false;
    let conFoco = false; // abierto de verdad (no fijado por el editor): foco atrapado y scroll bloqueado
    let devolver = null;
    let relojCierre = 0;

    const fijarPaso = (popup, paso) => {
      $$('[data-mt-popup-paso]', popup).forEach((el) => {
        el.hidden = el.dataset.mtPopupPaso !== paso;
      });
    };

    // La imagen se pide un poco antes de abrir (dentro de un popup oculto no se cargaría sola) para que la hoja no
    // aparezca con el hueco vacío. Si el archivo de respaldo no existe, se prueba el segundo y, si tampoco, se quita el hueco.
    const prepararImagen = (popup) => {
      const foto = $('[data-mt-popup-foto]', popup);
      const img = foto && $('img', foto);
      if (!img || foto.dataset.lista) return;
      foto.dataset.lista = '1';
      img.addEventListener('error', () => {
        const respaldo = foto.dataset.respaldo;
        if (respaldo && img.getAttribute('src') !== respaldo) {
          img.removeAttribute('srcset');
          img.src = respaldo;
        } else {
          popup.classList.add('es-sin-foto');
        }
      });
      img.loading = 'eager';
    };

    const abrir = (popup, { paso, fijo = false, recordar = true } = {}) => {
      if (!popup) return;
      clearTimeout(relojCierre);
      popup.classList.remove('es-saliendo');
      if (paso) fijarPaso(popup, paso);
      prepararImagen(popup);
      popup.classList.toggle('es-fijo', fijo);
      if (!popup.hidden) return;
      popup.hidden = false;
      abierto = true;
      if (fijo) return;
      marcar('bf_ventana_vista');
      conFoco = true;
      devolver = document.activeElement;
      { const hueco = window.innerWidth - html.clientWidth; html.style.setProperty('--mt-barra', `${hueco > 0 && hueco <= 20 ? hueco : 0}px`); }
      bloquearScroll(true);
      const caja = $('.mt-popup__caja', popup);
      if (caja) caja.focus({ preventScroll: true });
      if (recordar) aplazar(parseInt(popup.dataset.dias, 10) || 7);
    };
    const cerrar = (popup, { alInstante = false } = {}) => {
      if (!popup || popup.hidden) return;
      abierto = false;
      if (!$('[data-mt-popup-paso="ok"]:not([hidden])', popup)) marcar('bf_ventana_cerrada');
      const terminar = () => {
        popup.hidden = true;
        popup.classList.remove('es-saliendo', 'es-teclado');
        popup.style.removeProperty('--mt-teclado');
      };
      if (conFoco) {
        conFoco = false;
        bloquearScroll(false);
        if (devolver && devolver.isConnected && devolver !== document.body) devolver.focus({ preventScroll: true });
        devolver = null;
      }
      if (alInstante || reducido.matches) {
        terminar();
        return;
      }
      popup.classList.add('es-saliendo');
      relojCierre = setTimeout(terminar, 280);
    };

    // --- interacción (delegada: el editor puede volver a pintar la sección) ---
    document.addEventListener('click', (e) => {
      const popup = actual();
      if (!popup || popup.hidden) return;
      if (e.target.closest('[data-mt-popup-cerrar]')) {
        cerrar(popup);
        return;
      }
      const copiar = e.target.closest('[data-mt-popup-copiar]');
      if (!copiar) return;
      const texto = $('[data-mt-popup-copiar-texto]', copiar);
      const copiado = () => {
        if (!texto) return;
        texto.textContent = 'Copiado';
        setTimeout(() => {
          texto.textContent = 'Copiar';
        }, 2400);
      };
      const aMano = () => {
        // Sin permiso de portapapeles: se selecciona el código y se copia con el método antiguo.
        const valor = $('.mt-popup__codigo-valor', popup);
        if (!valor) return;
        const rango = document.createRange();
        rango.selectNodeContents(valor);
        const seleccion = window.getSelection();
        seleccion.removeAllRanges();
        seleccion.addRange(rango);
        try {
          if (document.execCommand('copy')) copiado();
        } catch (error) {}
      };
      if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(copiar.dataset.mtPopupCopiar).then(copiado, aMano);
      else aMano();
    });
    document.addEventListener('keydown', (e) => {
      if (!conFoco) return;
      const popup = actual();
      const caja = popup && !popup.hidden ? $('.mt-popup__caja', popup) : null;
      if (!caja) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        cerrar(popup);
      } else if (e.key === 'Tab' && (document.activeElement === caja || !caja.contains(document.activeElement))) {
        // El foco está en la propia ventana (recién abierta) o se ha escapado: entra por el extremo que toca.
        const focos = $$('a[href], button:not([disabled]), input:not([type="hidden"])', caja).filter((el) => el.offsetParent !== null);
        if (!focos.length) return;
        e.preventDefault();
        focos[e.shiftKey ? focos.length - 1 : 0].focus();
      } else {
        atraparFoco(caja, e);
      }
    });
    document.addEventListener('submit', (e) => {
      if (e.target.id === 'mt-popup-form') guardar('sessionStorage', ENVIADO, '1');
    });
    // 22/10 · Klaviyo: el alta se confirma sin recargar (iniciarKlaviyo). Solo con la respuesta buena se enseña «Casi está».
    document.addEventListener('mt:klaviyo-ok', (e) => {
      const popup = actual();
      if (!popup || !popup.contains(e.target)) return;
      aplazar(365);
      fijarPaso(popup, 'ok');
      const titulo = $('[data-mt-popup-ok-titulo]', popup);
      if (titulo) titulo.focus({ preventScroll: true });
    });
    // 22/10 · El mensaje de Black Friday de la barra abre la ventana aunque ya se hubiera cerrado (lo pide la persona).
    document.addEventListener('click', (e) => {
      const enlace = e.target.closest('[data-mt-popup-abrir]');
      if (!enlace) return;
      const popup = actual();
      if (!popup) return; // sin ventana (ya apuntado o fuera de fechas): sigue el enlace
      e.preventDefault();
      marcar('bf_barra_clic');
      abrir(popup, { paso: 'formulario', recordar: false });
    });

    // --- teclado en pantalla: la hoja sube lo que el teclado tapa y esconde la foto para que el campo quede a la vista ---
    const vista = window.visualViewport;
    if (vista) {
      const ajustar = () => {
        const popup = actual();
        if (!popup || popup.hidden) return;
        const campo = $('.mt-popup__correo', popup);
        const tapado = Math.max(0, Math.round(window.innerHeight - vista.height - vista.offsetTop));
        // Solo cuenta como teclado si se está escribiendo en el campo y la página no está ampliada con los dedos.
        const conTeclado = !!campo && document.activeElement === campo && vista.scale < 1.05 && tapado > 120;
        popup.style.setProperty('--mt-teclado', `${conTeclado ? tapado : 0}px`);
        popup.classList.toggle('es-teclado', conTeclado);
        if (!conTeclado) return;
        requestAnimationFrame(() => {
          const desliza = $('.mt-popup__desliza', popup);
          const boton = $('.mt-popup__campos [type="submit"]', popup);
          if (!desliza || !boton) return;
          const sobra = boton.getBoundingClientRect().bottom - desliza.getBoundingClientRect().bottom + 12;
          if (sobra > 0) desliza.scrollTop += sobra; // campo y botón, los dos a la vista
        });
      };
      vista.addEventListener('resize', ajustar);
      vista.addEventListener('scroll', ajustar);
    }

    // --- editor de temas: solo se ve (fijo) mientras la sección está seleccionada ---
    if (enEditor) {
      document.addEventListener('shopify:section:select', (e) => {
        const popup = $('[data-mt-popup]', e.target);
        if (popup) abrir(popup, { paso: popup.dataset.vista === 'ok' ? 'ok' : 'formulario', fijo: true });
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        const popup = $('[data-mt-popup]', e.target);
        if (popup) cerrar(popup, { alInstante: true });
      });
      return;
    }

    const popup = actual();
    if (!popup) return;

    // --- pruebas ---
    const parametros = new URLSearchParams(window.location.search);
    const prueba = parametros.get('mt-popup');
    if (prueba === 'reset') {
      guardar('localStorage', CLAVE, null);
      guardar('sessionStorage', ENVIADO, null);
    }
    if (prueba === '1' || prueba === 'ok') {
      abrir(popup, { paso: prueba === 'ok' ? 'ok' : 'formulario', recordar: false });
      return;
    }

    // --- vuelta del envío ---
    const enviadoAqui = leer('sessionStorage', ENVIADO) === '1';
    const apuntado = !!$('[data-mt-popup-paso="ok"][data-enviado]', popup) || parametros.get('customer_posted') === 'true';
    if (apuntado) {
      guardar('sessionStorage', ENVIADO, null);
      aplazar(365);
      if (enviadoAqui) abrir(popup, { paso: 'ok', recordar: false });
      return; // si se apuntó desde el pie, el popup ya no hace falta
    }
    if (enviadoAqui) {
      guardar('sessionStorage', ENVIADO, null);
      if ($('[data-mt-popup-error]', popup)) {
        abrir(popup, { paso: 'formulario' }); // el correo no era válido: se vuelve a enseñar con el aviso
        return;
      }
    }

    // --- aparición normal ---
    // 22/10 · Quien llega desde un correo de Klaviyo ya está en la lista: no se le vuelve a pedir el correo.
    if (parametros.has('_kx') || /klaviyo/i.test(parametros.get('utm_source') || '')) {
      aplazar(365);
      return;
    }
    if (enPlazo()) return;
    // 26/10 (auditoría, research/19 A2): en la ficha se decide la compra. Con «No salir sola en la ficha» la ventana no se
    // abre sola aquí; la barra de arriba («Black Friday · Avísame») la sigue abriendo al pulsarla.
    if (popup.dataset.ficha === 'nunca' && document.body.classList.contains('mt-plantilla--product')) return;
    const espera = (parseInt(popup.dataset.espera, 10) || 15) * 1000;
    const avance = Math.max(0, parseInt(popup.dataset.avance, 10) || 0) / 100;
    let tiempoCumplido = false;
    let avanceCumplido = avance === 0;
    let huboEspera = false;
    let libres = 0;

    // Otra capa abierta (menú, carrito de Dawn, ventanas, aviso de cookies de Shopify), pestaña oculta o alguien
    // escribiendo en un campo.
    const ocupado = () => {
      if (document.hidden || html.classList.contains('mt-bloqueo') || html.classList.contains('mt-menu-abierto')) return true;
      if (/(^|\s)overflow-hidden/.test(document.body.className)) return true;
      if ($('.mt-modal:not([hidden]), cart-drawer.active, .cart-notification.active, details[open] > .modal__content')) return true;
      const cookies = document.getElementById('shopify-pc__banner');
      if (cookies && cookies.getClientRects().length && getComputedStyle(cookies).visibility !== 'hidden') return true;
      const foco = document.activeElement;
      return !!foco && foco.matches('textarea, select, [contenteditable="true"], input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="hidden"])');
    };
    // 22/10 · Con algo en el carrito la persona está comprando: no se la interrumpe (se mira /cart.js una sola vez).
    let carrito = popup.hasAttribute('data-sin-carrito') ? 'pendiente' : 'da-igual';
    const intentar = () => {
      if (abierto || !tiempoCumplido || !avanceCumplido || !popup.isConnected) return;
      if (enPlazo()) return; // ya se vio en otra pestaña
      if (ocupado()) {
        huboEspera = true;
        libres = 0;
        setTimeout(intentar, 1000);
        return;
      }
      if (huboEspera && libres < 1) {
        libres += 1; // acaba de cerrarse otra capa: un respiro antes de aparecer
        setTimeout(intentar, 1200);
        return;
      }
      if (carrito === 'pendiente') {
        carrito = 'mirando';
        fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart.js`, { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .then((c) => {
            carrito = c && c.item_count > 0 ? 'lleno' : 'vacio';
            intentar();
          })
          .catch(() => {
            carrito = 'vacio';
            intentar();
          });
        return;
      }
      if (carrito === 'mirando' || carrito === 'lleno') return;
      abrir(popup, { paso: 'formulario' });
    };
    // La foto se pide unos segundos antes de la hora, y solo si la persona ya ha bajado lo suficiente.
    const inicio = Date.now();
    const adelanto = Math.max(0, espera - 3000);
    // 22/10 · En la ficha, con «Solo al ir a salir», no cuentan los segundos ni lo bajado: solo la salida (ratón por
    // arriba en escritorio; en el móvil, una subida rápida de más de media pantalla después de haber bajado un 35 %).
    const soloSalida = popup.dataset.ficha === 'salida' && document.body.classList.contains('mt-plantilla--product');
    const porSalida = () => {
      tiempoCumplido = true;
      avanceCumplido = true;
      prepararImagen(popup);
      intentar();
    };
    if (soloSalida) {
      if (!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches)) {
        let hondo = 0;
        let previo = window.scrollY;
        let tPrevio = Date.now();
        window.addEventListener(
          'scroll',
          () => {
            const y = window.scrollY;
            const t = Date.now();
            const recorrido = Math.max(1, html.scrollHeight - window.innerHeight);
            hondo = Math.max(hondo, y / recorrido);
            const subida = previo - y;
            if (subida > window.innerHeight * 0.5 && t - tPrevio < 450 && hondo >= 0.35 && t - inicio > 10000) porSalida();
            if (subida <= 0 || t - tPrevio > 450) {
              previo = y;
              tPrevio = t;
            }
          },
          { passive: true }
        );
      }
    }
    const alBajar = () => {
      const recorrido = html.scrollHeight - window.innerHeight;
      if (recorrido > 200 && window.scrollY / recorrido < avance) return;
      avanceCumplido = true;
      window.removeEventListener('scroll', alBajar);
      if (Date.now() - inicio >= adelanto) prepararImagen(popup);
      intentar();
    };
    if (!soloSalida) {
      if (!avanceCumplido) {
        window.addEventListener('scroll', alBajar, { passive: true });
        alBajar();
      }
      setTimeout(() => {
        if (avanceCumplido) prepararImagen(popup);
      }, adelanto);
      setTimeout(() => {
        tiempoCumplido = true;
        intentar();
      }, espera);
    }
    // 08/10 · en escritorio (ratón): si el cursor sale por arriba de la ventana (va a cerrar o a cambiar de pestaña),
    // el popup aparece sin esperar la cuenta, pasados 8 s en la página. En el móvil no hay gesto equivalente: ahí mandan
    // los segundos y el porcentaje de página bajada.
    if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.documentElement.addEventListener('mouseleave', (e) => {
        if (e.clientY > 0 || Date.now() - inicio < 8000) return;
        porSalida();
      });
    }
  }

  // 22/10 · Medición sin cookies: eventos de Clarity (si está cargado) para contar vistas, cierres, envíos y altas.
  function marcar(nombre) {
    try {
      if (typeof window.clarity === 'function') window.clarity('event', nombre);
    } catch (e) {}
  }

  /* ---------- 8b · altas en Klaviyo (22/10/2026) ---------- */
  // Cualquier <form data-mt-klaviyo> (la ventana de correo y, si la hay, la página de Black Friday) manda el correo a la
  // Client API de Klaviyo con la clave PÚBLICA (company_id) y la lista de Ajustes del tema → VEREDA · Black Friday.
  // La lista tiene doble confirmación: Klaviyo manda el correo para confirmar y, al confirmar, entra en Bienvenida.
  // Solo si Klaviyo responde 202 se dispara «mt:klaviyo-ok» (la ventana pasa a «Casi está»); si no, se enseña el error y
  // el botón vuelve a funcionar. Una trampa invisible («web») frena a los robots sin molestar a nadie.
  function iniciarKlaviyo() {
    const valido = (correo) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(correo);
    const parametros = new URLSearchParams(window.location.search);
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-mt-klaviyo]');
      if (!form) return;
      e.preventDefault();
      if (form.dataset.enviando === '1') return;
      const campo = $('input[type="email"]', form);
      const error = $('[data-mt-klaviyo-error]', form);
      const boton = $('[type="submit"]', form);
      const etiqueta = boton ? $('[data-mt-klaviyo-boton]', boton) : null;
      const correo = String((campo && campo.value) || '').trim().toLowerCase();
      const avisar = (texto) => {
        if (error) {
          error.textContent = texto;
          error.hidden = !texto;
        }
        if (campo) campo.toggleAttribute('aria-invalid', !!texto);
      };
      if (!valido(correo)) {
        avisar(form.dataset.errorCorreo || 'Revisa el correo.');
        if (campo) campo.focus();
        return;
      }
      avisar('');
      const trampa = form.elements.web;
      const exito = () => {
        form.dataset.hecho = '1';
        marcar(`${form.dataset.marca || 'bf_ventana'}_alta`);
        form.dispatchEvent(new CustomEvent('mt:klaviyo-ok', { bubbles: true }));
      };
      if (trampa && trampa.value) {
        exito(); // robot: se le dice que sí y no se manda nada
        return;
      }
      form.dataset.enviando = '1';
      const textoBoton = etiqueta ? etiqueta.textContent : '';
      if (boton) boton.disabled = true;
      if (etiqueta && boton.dataset.cargando) etiqueta.textContent = boton.dataset.cargando;
      marcar(`${form.dataset.marca || 'bf_ventana'}_envio`);
      const propiedades = { vereda_origen: form.dataset.fuente || 'VEREDA', vereda_pagina: window.location.pathname };
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((k) => {
        const v = parametros.get(k);
        if (v) propiedades[`vereda_${k}`] = v.slice(0, 120);
      });
      const cuerpo = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: form.dataset.fuente || 'VEREDA',
            profile: { data: { type: 'profile', attributes: { email: correo, properties: propiedades, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } }
          },
          relationships: { list: { data: { type: 'list', id: form.dataset.lista } } }
        }
      };
      const control = 'AbortController' in window ? new AbortController() : null;
      const reloj = control ? setTimeout(() => control.abort(), 12000) : 0;
      fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(form.dataset.empresa || '')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: '2025-07-15' },
        body: JSON.stringify(cuerpo),
        signal: control ? control.signal : undefined
      })
        .then((r) => {
          if (r.status !== 202 && !r.ok) throw new Error(`Klaviyo ${r.status}`);
          exito();
        })
        .catch(() => {
          marcar(`${form.dataset.marca || 'bf_ventana'}_error`);
          avisar(form.dataset.errorEnvio || 'No se ha podido guardar. Prueba otra vez.');
        })
        .finally(() => {
          clearTimeout(reloj);
          form.dataset.enviando = '';
          if (boton) boton.disabled = false;
          if (etiqueta) etiqueta.textContent = textoBoton;
        });
    });
  }

  /* ---------- 8c · cuenta atrás de Black Friday en la barra (22/10/2026) ---------- */
  // El mensaje [data-mt-campana] trae las horas (hora de la tienda, Europe/Madrid) y los textos de cada fase.
  // Antes de los últimos «cuenta-dias» enseña la fecha; después, «en 3 d 14 h»; durante la oferta, «quedan 2 d 5 h».
  // Nunca se reinicia: todo sale de las horas fijas de los ajustes. Pruebas: ?mt-campana-ahora=2026-11-27T10:00.
  function iniciarCampana() {
    const items = $$('[data-mt-campana]');
    if (!items.length) return;
    const ZONA = 'Europe/Madrid';
    const desfase = (ms) => {
      try {
        const p = new Intl.DateTimeFormat('en-US', { timeZone: ZONA, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(new Date(ms));
        const v = (t) => parseInt(p.find((x) => x.type === t).value, 10);
        return Date.UTC(v('year'), v('month') - 1, v('day'), v('hour') % 24, v('minute'), v('second')) - Math.floor(ms / 1000) * 1000;
      } catch (e) {
        return 3600000; // sin Intl: hora de invierno peninsular
      }
    };
    const aMs = (texto) => {
      const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(String(texto || ''));
      if (!m) return NaN;
      const local = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0));
      const primero = local - desfase(local);
      return local - desfase(primero);
    };
    const prueba = new URLSearchParams(window.location.search).get('mt-campana-ahora');
    const desvio = prueba && !Number.isNaN(aMs(prueba)) ? aMs(prueba) - Date.now() : 0;
    const resto = (ms) => {
      const min = Math.max(0, Math.floor(ms / 60000));
      const d = Math.floor(min / 1440);
      const h = Math.floor((min % 1440) / 60);
      const m = min % 60;
      if (d > 0) return `${d} d ${h} h`;
      if (h > 0) return `${h} h ${String(m).padStart(2, '0')} min`;
      return `${Math.max(1, m)} min`;
    };
    const pintar = () => {
      const ahora = Date.now() + desvio;
      items.forEach((li) => {
        const texto = $('.mt-anuncio__texto', li);
        if (!texto) return;
        const inicio = aMs(li.dataset.inicio);
        const fin = aMs(li.dataset.fin);
        const ant = aMs(li.dataset.anticipado);
        const dias = parseInt(li.dataset.cuentaDias, 10) || 0;
        let nuevo = '';
        if (Number.isNaN(inicio) || Number.isNaN(fin)) return;
        if (ahora > fin) {
          nuevo = 'El Black Friday ha terminado'; // solo lo ve quien tenía la página abierta a esa hora
        } else if (ahora >= inicio) {
          nuevo = (li.dataset.tActiva || '').replace('[cuenta]', `quedan ${resto(fin - ahora)}`);
        } else if (!Number.isNaN(ant) && ahora >= ant) {
          nuevo = (li.dataset.tAnticipado || '').replace('[cuenta]', `en ${resto(inicio - ahora)}`);
        } else if (dias > 0 && inicio - ahora <= dias * 864e5) {
          nuevo = (li.dataset.tCuenta || '').replace('[cuenta]', `en ${resto(inicio - ahora)}`);
        } else {
          nuevo = li.dataset.tPrevia || '';
        }
        if (nuevo && texto.textContent !== nuevo) texto.textContent = nuevo;
      });
    };
    pintar();
    setInterval(() => {
      if (!document.hidden) pintar();
    }, 15000);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) pintar();
    });
  }

  /* ---------- 9 · buscador de talla: del número de calzado a UNA talla ---------- */
  function iniciarTallas() {
    $$('[data-mt-tallas]').forEach((caja) => {
      const form = $('[data-mt-tallas-form]', caja);
      if (!form) return;
      const resultado = $('[data-mt-tallas-resultado]', caja);
      const grande = $('[data-mt-tallas-talla]', caja);
      const frase = $('[data-mt-tallas-frase]', caja);
      const aplicar = $('[data-mt-tallas-aplicar]', caja);
      const filas = $$('[data-talla]', caja);
      const rangos = String(caja.dataset.rangos || '')
        .split(',')
        .map((r) => {
          const [tramo, talla] = r.split(':').map((x) => (x || '').trim());
          const [min, max] = (tramo || '').split('-').map((x) => parseFloat(x));
          return { min, max: Number.isNaN(max) ? min : max, talla };
        })
        .filter((r) => r.talla && !Number.isNaN(r.min));
      let elegida = null;
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const numero = parseFloat(String($('input', form).value).replace(',', '.'));
        if (Number.isNaN(numero)) return;
        // Medio número entre dos tramos (38,5): se asigna al tramo superior.
        const enRango = (n) => rangos.find((r) => n >= r.min && n <= r.max);
        const rango = enRango(numero) || enRango(Math.ceil(numero));
        const texto = String(numero).replace('.', ',');
        resultado.hidden = false;
        filas.forEach((f) => f.classList.toggle('es-activa', !!rango && f.dataset.talla === rango.talla));
        elegida = rango ? rango.talla : null;
        grande.textContent = rango ? rango.talla : '—';
        frase.textContent = rango ? `Para un ${texto}, tu talla es la ${rango.talla}.` : `No tenemos talla para el ${texto}.`;
        if (aplicar) {
          aplicar.hidden = !rango;
          if (rango && aplicar.dataset.mtTallasAplicar !== 'enlace') aplicar.textContent = `Elegir la talla ${rango.talla}`;
        }
        // 20/10 (auditoría, Clarity): en el móvil el teclado se quedaba abierto y tapaba el resultado; hubo quien pulsó
        // «Ver mi talla» muchas veces seguidas. Se cierra el teclado y se enseña el resultado (o el botón de elegir).
        const campo = $('input', form);
        if (campo && document.activeElement === campo) campo.blur();
        const destino = aplicar && !aplicar.hidden ? aplicar : resultado;
        if (destino && destino.scrollIntoView) destino.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
      if (aplicar && aplicar.dataset.mtTallasAplicar !== 'enlace') {
        aplicar.addEventListener('click', () => {
          if (!elegida) return;
          document.dispatchEvent(new CustomEvent('mt:talla', { detail: { talla: elegida } }));
          const modal = caja.closest('.mt-modal');
          if (modal) cerrarModal(modal);
        });
      }
    });
  }

  /* ---------- 10 · carrito: abrir el cajón nativo de Dawn con el contenido nuevo ---------- */
  // Las secciones propias llaman a mtCarrito.anadir([{ id, quantity }]) y Dawn pinta el cajón.
  // v5.4: el regalo (producto a 0 €) entra solo con los calcetines y sale si ya no quedan; la protección de envío se pone
  // y se quita con su interruptor y se va si el carrito se queda sin calcetines; el botón «Añadir» del cajón suma los
  // pares que faltan para el siguiente par gratis.
  // 19/10: con el ajuste «Protección encendida de salida» (decisión del dueño, avisado del art. 60 bis) la protección entra
  // sola con los calcetines. Si el cliente la apaga, el carrito guarda el atributo `_proteccion_quitada` y ya no vuelve
  // en ese carrito (ni al cambiar de página ni al añadir más pares); al encenderla otra vez, se borra.
  // PUB_SUB_EVENTS es un const de constants.js (Dawn): está en el ámbito global, pero no cuelga de window.
  const eventosDawn = () => (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS);
  const raizTienda = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const esCarritoCompleto = (c) => !!c && Array.isArray(c.items) && typeof c.item_count === 'number';
  // 27/10: los pares del tobillero (ajuste «Otros calcetines que cuentan como pares») suman con los del principal.
  const esCalcetin = (i) => (tienda.productoIds || [tienda.productoId]).includes(i.product_id);
  const paresEn = (c) => c.items.filter(esCalcetin).reduce((s, i) => s + i.quantity, 0);
  let ultimoCarrito = null; // último carrito completo que conocemos (cart.js, cart/change.js o cart/update.js)
  const ATRIB_PROT = '_proteccion_quitada';
  // ¿Debe entrar sola la protección en este carrito? Solo con el ajuste encendido y si el cliente no la ha quitado.
  const protDeSalida = (c) => !!tienda.proteccion && !!tienda.proteccionSalida && !(c && c.attributes && c.attributes[ATRIB_PROT]);

  // Segundo regalo con tallas (separadores S / M): su talla sale de la del primer par de calcetines, pasada por el ajuste
  // «talla del calcetín:talla del regalo» (p. ej. «S:S,M:M,L:M»). Si no hay pista, la primera talla en venta.
  const esTalla = (nombre) => /talla|size|tama/i.test(String(nombre || ''));
  const esColor = (nombre) => /colou?r/i.test(String(nombre || ''));
  // 06/10 · color del regalo: el que se marcó en la cajita del pack (sessionStorage); sin elegir, el primero en venta.
  const CLAVE_COLOR2 = 'mt-regalo2-color';
  function colorRegalo2() {
    try {
      return sessionStorage.getItem(CLAVE_COLOR2) || null;
    } catch (e) {
      return null;
    }
  }
  const variantesRegalo2 = () => (Array.isArray(tienda.regalo2Variantes) ? tienda.regalo2Variantes : []);
  // Variante del regalo con esa talla y ese color; si no la hay, la que encaje en lo que se pueda.
  function buscarRegalo2(talla, color) {
    const nombres = Array.isArray(tienda.regalo2Opciones) ? tienda.regalo2Opciones : [];
    const iTalla = nombres.findIndex(esTalla);
    const iColor = nombres.findIndex(esColor);
    const vale = (v, t, c) => (!t || iTalla < 0 || v.opciones[iTalla] === t) && (!c || iColor < 0 || v.opciones[iColor] === c);
    const lista = variantesRegalo2();
    return lista.find((v) => vale(v, talla, color)) || lista.find((v) => vale(v, talla, null)) || lista.find((v) => vale(v, null, color)) || null;
  }
  function varianteRegalo2(tallaCalcetin) {
    const mapa = {};
    String(tienda.regalo2Tallas || '').split(',').forEach((par) => {
      const [c, r] = par.split(':').map((x) => (x || '').trim());
      if (c && r) mapa[c.toLowerCase()] = r;
    });
    const talla = tallaCalcetin ? mapa[String(tallaCalcetin).toLowerCase()] : null;
    const v = buscarRegalo2(talla, colorRegalo2());
    return (v && v.id) || tienda.regalo2;
  }
  function tallaPrimerPar(c) {
    const linea = c && c.items.find(esCalcetin);
    if (!linea) return null;
    const opciones = linea.options_with_values || [];
    const o = opciones.find((x) => esTalla(x.name)) || opciones[0];
    return o ? o.value : linea.variant_title;
  }
  // Talla de una variante de la ficha (para el regalo que entra en la misma petición que los pares).
  function tallaDeVariante(id) {
    const s = document.querySelector('[data-mt-producto] [data-mt-datos]');
    if (!s) return null;
    try {
      const d = JSON.parse(s.textContent);
      const v = (d.variantes || []).find((x) => Number(x.id) === Number(id));
      if (!v) return null;
      const k = (d.opciones || []).findIndex(esTalla);
      return v.opciones[k >= 0 ? k : 0];
    } catch (e) {
      return null;
    }
  }

  async function pedirCarrito(ruta, cuerpo) {
    const cajon = $('cart-drawer');
    if (cajon) {
      cuerpo.sections = cajon.getSectionsToRender().map((s) => s.id);
      cuerpo.sections_url = window.location.pathname;
    }
    const respuesta = await fetch(`${raizTienda()}${ruta}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(cuerpo),
    });
    const datos = await respuesta.json();
    if (!respuesta.ok || datos.status) {
      const error = new Error(datos.description || datos.message || 'No se pudo actualizar el carrito.');
      error.datos = datos;
      throw error;
    }
    if (esCarritoCompleto(datos)) ultimoCarrito = datos;
    else if (ruta.startsWith('cart/add')) ultimoCarrito = null; // add.js solo devuelve lo añadido
    return datos;
  }

  // Pinta el cajón con las secciones nuevas SIN abrirlo (el renderContents de Dawn siempre lo abre).
  function pintarCajon(datos) {
    const cajon = $('cart-drawer');
    if (!cajon || !datos || !datos.sections) return;
    cajon.getSectionsToRender().forEach((s) => {
      const el = s.selector ? document.querySelector(s.selector) : document.getElementById(s.id);
      const html = datos.sections[s.id];
      if (el && html) el.innerHTML = cajon.getSectionInnerHTML(html, s.selector);
    });
    if (esCarritoCompleto(datos)) {
      cajon.classList.toggle('is-empty', datos.item_count === 0);
      const items = $('cart-drawer-items');
      if (items) items.classList.toggle('is-empty', datos.item_count === 0);
    }
  }

  function avisarCambio(datos) {
    if (typeof window.publish === 'function' && eventosDawn()) {
      window.publish(eventosDawn().cartUpdate, { source: 'mt-carrito', cartData: datos });
    }
  }

  // Deja el regalo y la protección como deben estar según los calcetines que haya en el carrito.
  let revisando = false;
  let otraVez = false; // 19/10 (auditoría P1-1): si llega otra revisión mientras hay una en marcha, se repite al acabar
  async function revisarCarrito(carrito) {
    if (!tienda.regaloProducto && !tienda.proteccionProducto && !tienda.regalo2Producto && !tienda.pruebaProducto) return;
    if (revisando) {
      otraVez = true;
      return;
    }
    revisando = true;
    try {
      let c = esCarritoCompleto(carrito) ? carrito : null;
      if (!c) c = await fetch(`${raizTienda()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      ultimoCarrito = c;
      const pares = paresEn(c);
      const regalo = c.items.find((i) => tienda.regaloProducto && i.product_id === tienda.regaloProducto);
      const prot = c.items.find((i) => tienda.proteccionProducto && i.product_id === tienda.proteccionProducto);
      // Segundo regalo (como las taloneras de Nubelina): solo desde cierto número de pares.
      const regalo2 = c.items.find((i) => tienda.regalo2Producto && i.product_id === tienda.regalo2Producto);
      const quiereRegalo2 = !!tienda.regalo2 && pares >= (tienda.regalo2Desde || 3);
      const updates = {};
      if (regalo2) {
        if (!quiereRegalo2) updates[regalo2.key] = 0;
        else if (regalo2.quantity !== 1) updates[regalo2.key] = 1;
      }
      if (regalo) {
        if (pares === 0 || !tienda.regalo) updates[regalo.key] = 0;
        else if (regalo.quantity !== 1) updates[regalo.key] = 1;
      }
      if (prot) {
        if (pares === 0) updates[prot.key] = 0;
        else if (prot.quantity !== 1) updates[prot.key] = 1;
      }
      // 25/10 · par de prueba: uno por pedido. Si alguien sube la cantidad o mete otra talla, se deja solo la primera línea y en 1.
      if (tienda.pruebaProducto) {
        c.items.filter((i) => i.product_id === tienda.pruebaProducto).forEach((linea, n) => {
          if (n > 0) updates[linea.key] = 0;
          else if (linea.quantity !== 1) updates[linea.key] = 1;
        });
      }
      let datos = null;
      if (Object.keys(updates).length) datos = await pedirCarrito('cart/update.js', { updates });
      const faltan = [];
      if (tienda.regalo && pares > 0 && !regalo) faltan.push({ id: tienda.regalo, quantity: 1 });
      if (quiereRegalo2 && !regalo2) faltan.push({ id: varianteRegalo2(tallaPrimerPar(c)), quantity: 1 });
      if (pares > 0 && !prot && protDeSalida(c)) faltan.push({ id: tienda.proteccion, quantity: 1 });
      if (faltan.length) datos = await pedirCarrito('cart/add.js', { items: faltan });
      if (datos) {
        pintarCajon(datos);
        avisarCambio(datos);
      }
    } catch (e) {
      /* si falla, el carrito sigue funcionando: solo no se ajustan regalo ni protección */
    } finally {
      revisando = false;
      if (otraVez) {
        otraVez = false;
        revisarCarrito(); // sin carrito: vuelve a leer /cart.js, con los cambios que llegaron mientras tanto
      }
    }
  }

  // 26/10 (research/19 A7): al abrirse el cajón tras añadir, «Añadido ✓ · 2 pares, talla L» bajo «Tu carrito». Hubo quien
  // añadió tres veces seguidas (#1015) porque no veía claro que ya estaba dentro. Se va con el siguiente cambio del cajón.
  function avisoAnadido(cajon, datos, items) {
    try {
      const pares = items.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
      if (!pares) return;
      const ids = items.map((it) => Number(it.id));
      const tallas = [...new Set((datos.items || []).filter((i) => ids.includes(Number(i.variant_id || i.id))).map((i) => String(i.variant_title || '').split(' / ')[0].trim()).filter(Boolean))];
      const cuantos = pares === 1 ? '1 par' : `${pares} pares`;
      const detalle = tallas.length === 1 ? `, talla ${tallas[0]}` : tallas.length > 1 ? ` (${tallas.join(' y ')})` : '';
      const cab = cajon.querySelector('.drawer__header');
      if (!cab) return;
      const viejo = cajon.querySelector('.mt-cajon-anadido');
      if (viejo) viejo.remove();
      const aviso = document.createElement('p');
      aviso.className = 'mt-cajon-anadido';
      aviso.setAttribute('role', 'status');
      aviso.textContent = `Añadido ✓ · ${cuantos}${detalle}`;
      cab.after(aviso);
    } catch (e) {
      /* sin aviso, el cajón funciona igual */
    }
  }

  window.mtCarrito = {
    async anadir(items) {
      const lista = items.slice();
      // Si ya sabemos que el regalo no está, entra en la misma petición (sin segundo viaje ni parpadeo).
      if (tienda.regalo && ultimoCarrito && !ultimoCarrito.items.some((i) => i.product_id === tienda.regaloProducto)) {
        lista.push({ id: tienda.regalo, quantity: 1 });
      }
      if (tienda.regalo2 && ultimoCarrito && !ultimoCarrito.items.some((i) => i.product_id === tienda.regalo2Producto)) {
        const nuevos = items.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
        if (paresEn(ultimoCarrito) + nuevos >= (tienda.regalo2Desde || 3)) {
          const talla = tallaPrimerPar(ultimoCarrito) || tallaDeVariante(items[0] && items[0].id);
          lista.push({ id: varianteRegalo2(talla), quantity: 1 });
        }
      }
      if (ultimoCarrito && protDeSalida(ultimoCarrito) && !ultimoCarrito.items.some((i) => i.product_id === tienda.proteccionProducto)) {
        lista.push({ id: tienda.proteccion, quantity: 1 });
      }
      const cajon = $('cart-drawer');
      const datos = await pedirCarrito('cart/add.js', { items: lista });
      if (cajon && datos.sections) {
        cajon.classList.remove('is-empty'); // igual que el formulario de producto de Dawn: el cajón deja de estar vacío
        cajon.renderContents(datos);
        avisoAnadido(cajon, datos, items);
      } else {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      }
      if (typeof window.publish === 'function' && eventosDawn()) {
        window.publish(eventosDawn().cartUpdate, { source: 'mt-producto', cartData: datos });
      }
      return datos;
    },
    revisar: revisarCarrito,
  };

  function iniciarCarrito() {
    // Interruptor de la protección de envío. En fase de captura y sin propagar: el cajón de Dawn (cart-items) trata
    // cualquier «change» de dentro como un cambio de cantidad.
    document.addEventListener('change', async (e) => {
      const t = e.target.closest && e.target.closest('[data-mt-proteccion]');
      if (!t) return;
      e.stopPropagation();
      const quiere = t.checked;
      t.disabled = true;
      try {
        // Con la protección de salida, el carrito recuerda si el cliente la quitó (una sola petición: cantidad + atributo).
        const datos = tienda.proteccionSalida
          ? await pedirCarrito('cart/update.js', {
              updates: quiere ? { [t.dataset.variante]: 1 } : { [t.dataset.linea]: 0 },
              attributes: { [ATRIB_PROT]: quiere ? '' : '1' },
            })
          : quiere
          ? await pedirCarrito('cart/add.js', { items: [{ id: Number(t.dataset.variante), quantity: 1 }] })
          : await pedirCarrito('cart/change.js', { id: t.dataset.linea, quantity: 0 });
        // 22/10: en la página del carrito el interruptor va en el pie (resumen), que el cajón no vuelve a pintar: se recarga entera,
        // como con la talla del segundo regalo (si no, el total se queda viejo y el interruptor bloqueado).
        if (document.getElementById('main-cart-items')) {
          window.location.reload();
          return;
        }
        pintarCajon(datos);
        avisarCambio(datos);
      } catch (error) {
        t.checked = !quiere;
        t.disabled = false;
      }
    }, true);
    // Talla del segundo regalo, desde su línea del carrito: se quita la línea y entra la otra talla (dos pasos que funcionan
    // igual en la tienda y en el visor). En fase de captura y sin propagar, como la protección.
    document.addEventListener('change', async (e) => {
      const s = e.target.closest && e.target.closest('[data-mt-regalo2-talla]');
      if (!s) return;
      e.stopPropagation();
      const nueva = Number(s.value);
      if (!nueva || String(nueva) === s.dataset.actual) return;
      s.disabled = true;
      try {
        await pedirCarrito('cart/change.js', { id: s.dataset.linea, quantity: 0 });
        const datos = await pedirCarrito('cart/add.js', { items: [{ id: nueva, quantity: 1 }] });
        if (document.getElementById('main-cart-items')) {
          window.location.reload(); // página del carrito: se vuelve a pintar entera
          return;
        }
        pintarCajon(datos);
        avisarCambio(datos);
      } catch (error) {
        s.value = s.dataset.actual;
        s.disabled = false;
      }
    }, true);
    // «Añadir N pares» del cajón (siguiente par gratis)
    document.addEventListener('click', async (e) => {
      const b = e.target.closest && e.target.closest('[data-mt-anadir-pares]');
      if (!b) return;
      e.preventDefault();
      b.classList.add('es-cargando');
      b.disabled = true;
      try {
        const datos = await pedirCarrito('cart/add.js', { items: [{ id: Number(b.dataset.variante), quantity: parseInt(b.dataset.cantidad, 10) || 1 }] });
        pintarCajon(datos);
        avisarCambio(datos);
        // 19/10: el aviso va con origen «mt-carrito» y la revisión lo ignora; sin esto, el regalo que llega con más pares
        // (separadores desde 3) no entraba hasta cambiar de página.
        revisarCarrito();
      } catch (error) {
        b.classList.remove('es-cargando');
        b.disabled = false;
      }
    });
    // 06/10 · color de los separadores de regalo, desde las cajitas de los packs: se guarda, se copia en las demás cajitas y, si el
    // regalo ya está en el carrito con otro color, se cambia allí (quitar y poner, como la talla en su línea), con su misma talla.
    const pintarColorRegalo2 = (color) => {
      $$('[data-mt-regalo2-colores]').forEach((grupo) => {
        let esta = false;
        $$('[data-mt-regalo2-color]', grupo).forEach((r) => {
          const si = r.value === color;
          if (si) esta = true;
          r.checked = si;
          r.closest('.mt-color').classList.toggle('es-elegido', si);
        });
        const nombre = $('[data-mt-regalo2-color-nombre]', grupo);
        if (nombre && esta) nombre.textContent = color;
      });
    };
    const guardado = colorRegalo2();
    if (guardado && $$('[data-mt-regalo2-color]').some((r) => r.value === guardado)) pintarColorRegalo2(guardado);
    document.addEventListener('change', async (e) => {
      const r = e.target.closest && e.target.closest('[data-mt-regalo2-color]');
      if (!r) return;
      const color = r.value;
      try {
        sessionStorage.setItem(CLAVE_COLOR2, color);
      } catch (error) {
        /* sin sessionStorage: vale para esta página */
      }
      pintarColorRegalo2(color);
      try {
        const c = ultimoCarrito || (await fetch(`${raizTienda()}cart.js`, { headers: { Accept: 'application/json' } }).then((x) => x.json()));
        const linea = c && c.items && c.items.find((i) => tienda.regalo2Producto && i.product_id === tienda.regalo2Producto);
        if (!linea) return;
        const talla = ((linea.options_with_values || []).find((o) => esTalla(o.name)) || {}).value || null;
        const destino = buscarRegalo2(talla, color);
        if (!destino || Number(destino.id) === Number(linea.variant_id)) return;
        await pedirCarrito('cart/change.js', { id: linea.key, quantity: 0 });
        const datos = await pedirCarrito('cart/add.js', { items: [{ id: destino.id, quantity: 1 }] });
        pintarCajon(datos);
        avisarCambio(datos);
      } catch (error) {
        /* si falla, el regalo se queda con el color que tenía y se puede cambiar en el carrito */
      }
    });
    // Cambios hechos por Dawn (cantidades, quitar) o por otros formularios → revisar regalo y protección
    if (typeof window.subscribe === 'function' && eventosDawn()) {
      window.subscribe(eventosDawn().cartUpdate, (evento) => {
        if (evento && evento.source === 'mt-carrito') return;
        revisarCarrito(evento && evento.cartData);
      });
    }
    revisarCarrito();
  }

  /* ---------- carriles (carruseles con scroll nativo) ----------
     Marcado: [data-mt-carril] > [data-mt-carril-pista] + botones [data-mt-carril-ir="-1|1"] + puntos [data-mt-carril-puntos].
     El dedo arrastra con el scroll del navegador; las flechas avanzan una tarjeta. Sin JS sigue siendo un scroll horizontal. */
  function iniciarCarriles(raiz = document) {
    $$('[data-mt-carril]', raiz).forEach((carril) => {
      if (carril.mtCarril) return; carril.mtCarril = true;
      const pista = $('[data-mt-carril-pista]', carril); if (!pista) return;
      const flechas = $$('[data-mt-carril-ir]', carril);
      const puntos = $('[data-mt-carril-puntos]', carril);
      const piezas = () => Array.from(pista.children);
      const paso = () => { const p = piezas(); return p.length > 1 ? p[1].offsetLeft - p[0].offsetLeft : pista.clientWidth; };
      if (puntos) piezas().forEach((_, i) => { const b = document.createElement('span'); b.className = 'mt-carril__punto' + (i === 0 ? ' es-activo' : ''); puntos.appendChild(b); });
      const pintar = () => {
        const max = pista.scrollWidth - pista.clientWidth;
        const hay = max > 4;
        carril.classList.toggle('es-fijo', !hay);
        flechas.forEach((f) => { const d = parseInt(f.dataset.mtCarrilIr, 10); f.hidden = !hay; f.disabled = d < 0 ? pista.scrollLeft <= 2 : pista.scrollLeft >= max - 2; });
        if (puntos) { const i = Math.round(pista.scrollLeft / Math.max(1, paso())); $$('.mt-carril__punto', puntos).forEach((b, n) => b.classList.toggle('es-activo', n === Math.min(i, puntos.children.length - 1))); puntos.hidden = !hay; }
      };
      flechas.forEach((f) => f.addEventListener('click', () => pista.scrollBy({ left: parseInt(f.dataset.mtCarrilIr, 10) * paso(), behavior: reducido.matches ? 'auto' : 'smooth' })));
      let tic = 0; pista.addEventListener('scroll', () => { if (tic) return; tic = requestAnimationFrame(() => { tic = 0; pintar(); }); }, { passive: true });
      window.addEventListener('resize', pintar, { passive: true });
      pintar();
    });
  }
  window.mtCarriles = iniciarCarriles;

  /* ---------- 26/10 · Aviso de cookies sin responder (auditoría, research/19 A1) ----------
     Mientras nadie lo responde, el aviso de Shopify se queda fijo abajo y tapaba la barra «Añadir», WhatsApp y el pie del
     cajón («Total» y «Pago seguro»). Su altura va a --mt-aviso-alto (0 cuando se cierra) y el CSS los sube por encima.
     El aviso no se toca: ni textos, ni botones, ni lo que guarda. */
  function iniciarAvisoCookies() {
    let tic = 0;
    let visto = null;
    const medir = () => {
      tic = 0;
      const aviso = document.getElementById('shopify-pc__banner');
      let alto = 0;
      if (aviso && aviso.getClientRects().length) {
        const s = getComputedStyle(aviso);
        const r = aviso.getBoundingClientRect();
        if (s.visibility !== 'hidden' && +s.opacity > 0.05 && r.height > 0 && r.top < window.innerHeight) alto = Math.max(0, Math.round(window.innerHeight - r.top));
      }
      html.style.setProperty('--mt-aviso-alto', `${alto}px`);
      html.classList.toggle('mt-con-aviso', alto > 0);
      if (aviso && aviso !== visto && 'ResizeObserver' in window) {
        visto = aviso;
        new ResizeObserver(pedir).observe(aviso);
      }
    };
    const pedir = () => {
      if (!tic) tic = requestAnimationFrame(medir);
    };
    // Shopify lo pinta tarde y lo quita (o lo esconde) al responder, y el enlace del pie lo vuelve a abrir.
    new MutationObserver(pedir).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
    document.addEventListener('visitorConsentCollected', () => setTimeout(medir, 50));
    document.addEventListener('click', (e) => {
      if (e.target.closest && e.target.closest('#shopify-pc__banner')) setTimeout(medir, 400);
    });
    window.addEventListener('resize', pedir, { passive: true });
    medir();
  }

  /* ---------- arranque ---------- */
  function iniciar() {
    iniciarAvisoCookies();
    iniciarCarrito();
    iniciarCarriles();
    iniciarReveal();
    iniciarCabecera();
    iniciarAnuncio();
    iniciarMenu();
    iniciarModales();
    iniciarAcordeones();
    iniciarParallax();
    iniciarKlaviyo();
    iniciarPopup();
    iniciarCampana();
    iniciarTallas();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar);
  else iniciar();

  // Editor de temas de Shopify: al recargar una sección, se vuelven a activar sus reveals.
  document.addEventListener('shopify:section:load', (e) => {
    iniciarReveal(e.target);
    iniciarCarriles(e.target);
    $$('.mt-rev', e.target).forEach((el) => el.classList.add('es-visible'));
  });
  window.mtReveal = iniciarReveal;
})();
